import React, { useMemo, useState } from "react";
import { Box, Chip, MenuItem, Select, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import MyContainer from "../../../MyContainer";
import ProductCard from "../../../ui/ProductCard";
import Reveal from "../../../ui/Reveal";
import { useShopCatalogSectionStyles } from "./ShopCatalogSection.styles";
import { useI18n } from "../../../../i8n/useI18n";
import { useCommerceData } from "../../../../hooks/useCommerceData";
import { commerceRepository } from "../../../../api";
import { ProductSortKey } from "../../../../api/types";

interface ShopCatalogSectionProps {
    category?: string;
}

const PRICE_CEILINGS = [25, 50, 100];

const ShopCatalogSection = (props: ShopCatalogSectionProps) => {
    const { classes } = useShopCatalogSectionStyles();
    const { t } = useI18n();

    const [productType, setProductType] = useState<string | null>(null);
    const [priceMax, setPriceMax] = useState<number | null>(null);
    const [sortKey, setSortKey] = useState<ProductSortKey>("CREATED");

    const { data: productTypes } = useCommerceData(
        () => commerceRepository.getProductTypes(props.category),
        [props.category]
    );

    const { data: collections } = useCommerceData(() => commerceRepository.getCollections(), []);

    const { data } = useCommerceData(
        () =>
            commerceRepository.getProducts({
                category: props.category,
                productType: productType ?? undefined,
                priceMax: priceMax ?? undefined,
                sortKey,
            }),
        [props.category, productType, priceMax, sortKey]
    );

    const categoryChips = useMemo(
        () => [
            { label: t("nav.shop"), href: "/shop", active: !props.category },
            ...(collections ?? []).map((collection) => ({
                label: collection.title,
                href: `/shop/${collection.handle}`,
                active: props.category === collection.handle,
            })),
        ],
        [props.category, t, collections]
    );

    const hasActiveFilters = !!productType || priceMax !== null;

    const resetFilters = () => {
        setProductType(null);
        setPriceMax(null);
    };

    return (
        <MyContainer>
            <Box className={classes.layout}>
                <Box className={classes.sidebar} component="aside">
                    <Box className={classes.filterGroup}>
                        <Typography className={classes.filterGroupTitle}>{t("shopPage.filters.category")}</Typography>
                        <Box className={classes.chipRow}>
                            {categoryChips.map((chip) => (
                                <Chip
                                    key={chip.href}
                                    component={RouterLink}
                                    to={chip.href}
                                    label={chip.label}
                                    clickable
                                    color={chip.active ? "primary" : "default"}
                                    variant={chip.active ? "filled" : "outlined"}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box className={classes.filterGroup}>
                        <Typography className={classes.filterGroupTitle}>{t("shopPage.filters.productType")}</Typography>
                        <Box className={classes.chipRow}>
                            {productTypes?.map((type) => (
                                <Chip
                                    key={type}
                                    label={type}
                                    clickable
                                    onClick={() => setProductType(productType === type ? null : type)}
                                    color={productType === type ? "primary" : "default"}
                                    variant={productType === type ? "filled" : "outlined"}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box className={classes.filterGroup}>
                        <Typography className={classes.filterGroupTitle}>{t("shopPage.filters.priceRange")}</Typography>
                        <Box className={classes.chipRow}>
                            {PRICE_CEILINGS.map((ceiling) => (
                                <Chip
                                    key={ceiling}
                                    label={t("shopPage.filters.priceUpTo").replace("{value}", String(ceiling))}
                                    clickable
                                    onClick={() => setPriceMax(priceMax === ceiling ? null : ceiling)}
                                    color={priceMax === ceiling ? "primary" : "default"}
                                    variant={priceMax === ceiling ? "filled" : "outlined"}
                                />
                            ))}
                        </Box>
                    </Box>

                    {hasActiveFilters && (
                        <Chip
                            className={classes.resetButton}
                            label={t("shopPage.filters.reset")}
                            onClick={resetFilters}
                            variant="outlined"
                        />
                    )}
                </Box>

                <Box>
                    <Box className={classes.toolbar}>
                        <Typography className={classes.resultsCount}>
                            {t("shopPage.resultsCount").replace("{count}", String(data?.items.length ?? 0))}
                        </Typography>
                        <Select
                            size="small"
                            value={sortKey}
                            onChange={(e) => setSortKey(e.target.value as ProductSortKey)}
                        >
                            <MenuItem value="CREATED">{t("shopPage.filters.sortOptions.newest")}</MenuItem>
                            <MenuItem value="PRICE_ASC">{t("shopPage.filters.sortOptions.priceAsc")}</MenuItem>
                            <MenuItem value="PRICE_DESC">{t("shopPage.filters.sortOptions.priceDesc")}</MenuItem>
                            <MenuItem value="TITLE">{t("shopPage.filters.sortOptions.nameAsc")}</MenuItem>
                        </Select>
                    </Box>

                    {data?.items.length === 0 ? (
                        <Typography className={classes.noResults}>{t("shopPage.noResults")}</Typography>
                    ) : (
                        <Box className={classes.grid}>
                            {data?.items.map((product, index) => (
                                <Reveal key={product.id} delay={(index % 6) * 0.05}>
                                    <ProductCard product={product} />
                                </Reveal>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </MyContainer>
    );
};

export default ShopCatalogSection;
